import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/Icon';
import { startOperation } from '../telemetry/client';
import { useLocale, useTranslation } from '../../i18n/react';
import { canUseUpscaler, defaultUpscaleLimits, outputFilename, planUpscale, validateUpscaleInput, type ImageSize, type UpscaleCapabilities, type UpscaleScale } from './domain';
import { decodeUpscaleImage, triggerUpscaleDownload, verifyUpscaleOutput } from './media';
import { upscalerRepository, waitForPoll } from './repository';
import '../converter/converter.css';
import './upscaler.css';

type Source = { file: File; size: ImageSize; url: string };
type Download = { url: string; name: string };
export function ImageUpscalerCard() {
  const tr = useTranslation();
  const locale = useLocale();
  const canonicalHref = upscalerRepository.canonicalHref ? `https://designforge.site/${locale === 'en' ? 'en/' : ''}tools/ai-image-upscaler/` : undefined;
  const input = useRef<HTMLInputElement>(null);
  const mounted = useRef(false);
  const selection = useRef(0);
  const operation = useRef<{ abort: AbortController; receipt?: string; cancelling?: boolean } | null>(null);
  const previewUrl = useRef<string | null>(null);
  const resultUrl = useRef<string | null>(null);
  const [capabilities, setCapabilities] = useState<UpscaleCapabilities | null>(null);
  const refreshCapabilities = useRef<() => void>(() => {});
  const [checking, setChecking] = useState(true);
  const [source, setSource] = useState<Source | null>(null);
  const [reading, setReading] = useState(false);
  const [model, setModel] = useState('realesrgan');
  const [scale, setScale] = useState<UpscaleScale>(2);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('');
  const [feedback, setFeedback] = useState<{ message: string; error?: boolean } | null>(null);
  const [download, setDownload] = useState<Download | null>(null);
  const limits = capabilities?.limits ?? defaultUpscaleLimits;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false; selection.current++;
      const current = operation.current;
      current?.abort.abort();
      if (current?.receipt) void upscalerRepository.cancel(current.receipt).catch(() => {});
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
      if (resultUrl.current) URL.revokeObjectURL(resultUrl.current);
    };
  }, []);
  useEffect(() => {
    let disposed = false;
    let pending = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let abort: AbortController | undefined;
    async function load() {
      if (disposed || pending) return;
      pending = true; clearTimeout(timer); setChecking(true);
      abort = new AbortController();
      try {
        const data = await upscalerRepository.capabilities(abort.signal);
        if (disposed) return;
        setCapabilities(data);
        setModel(current => data.models.some(item => item.id === current) ? current : data.models[0]?.id ?? 'realesrgan');
      } catch {
        if (!disposed) setCapabilities({ available: false, reason: 'budget_unavailable', models: [], limits: defaultUpscaleLimits, budget: null });
      } finally {
        pending = false;
        if (!disposed) { setChecking(false); timer = setTimeout(() => void load(), 60000); }
      }
    }
    const refresh = () => { void load(); };
    const visible = () => { if (document.visibilityState === 'visible') refresh(); };
    refreshCapabilities.current = refresh;
    document.addEventListener('visibilitychange', visible);
    refresh();
    return () => {
      disposed = true; abort?.abort(); clearTimeout(timer);
      document.removeEventListener('visibilitychange', visible);
      refreshCapabilities.current = () => {};
    };
  }, []);
  function clearDownload() {
    if (resultUrl.current) URL.revokeObjectURL(resultUrl.current);
    resultUrl.current = null; setDownload(null);
  }
  async function choose(files: File[]) {
    if (busy || !files.length) return;
    if (files.length !== 1) { setFeedback({ message: 'Mỗi lần chỉ chọn một ảnh.', error: true }); return; }
    const file = files[0];
    const version = ++selection.current;
    setReading(true); setFeedback(null);
    try {
      // Reject byte/type limits before asking the browser to decode large content.
      validateUpscaleInput(file, { width: 1, height: 1 }, 2, limits);
      const size = await decodeUpscaleImage(file);
      validateUpscaleInput(file, size, 2, limits);
      if (!mounted.current || version !== selection.current) return;
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
      const url = URL.createObjectURL(file); previewUrl.current = url;
      clearDownload(); setSource({ file, size, url });
    } catch (error) {
      if (mounted.current && version === selection.current) setFeedback({ message: error instanceof Error && error.name !== 'InvalidStateError' ? error.message : 'Không đọc được ảnh.', error: true });
    } finally { if (mounted.current && version === selection.current) setReading(false); }
  }
  let validation = '';
  if (source) try { validateUpscaleInput(source.file, source.size, scale, limits); } catch (error) { validation = (error as Error).message; }
  const plan = source && !validation ? planUpscale(source.size, scale) : null;
  const availableScales = capabilities?.models.find(item => item.id === model)?.scales ?? [2, 4];
  const available = canUseUpscaler(capabilities) && !checking;
  async function start() {
    if (!source || !plan || !available || operation.current || validation) return;
    const telemetry = startOperation('ai-image-upscaler');
    const current = { abort: new AbortController(), receipt: undefined as string | undefined, cancelling: false };
    operation.current = current; setBusy(true); setFeedback(null); clearDownload(); setStage('Đang xử lý, vui lòng đợi');
    try {
      const job = await upscalerRepository.create(source.file, model, scale, crypto.randomUUID(), current.abort.signal);
      current.receipt = job.receipt;
      refreshCapabilities.current();
      if (current.abort.signal.aborted) { void upscalerRepository.cancel(job.receipt).catch(() => {}); return; }
      const deadline = Math.min(Date.parse(job.expiresAt), Date.now() + 15 * 60000);
      setStage('Đang chờ GPU…');
      for (;;) {
        if (Date.now() >= deadline) throw new Error('Phiên xử lý đã hết hạn. Hãy thử lại.');
        const status = await upscalerRepository.status(job.receipt, current.abort.signal);
        if (status === 'failed') throw new Error('Không thể xử lý ảnh. Vui lòng thử lại.');
        if (status === 'cancelled') throw new Error('Yêu cầu đã được hủy.');
        if (status === 'succeeded') break;
        if (mounted.current) setStage(status === 'running' ? 'Đang upscale trên máy chủ…' : 'Đang chờ GPU…');
        await waitForPoll(current.abort.signal);
      }
      if (mounted.current) setStage('Đang kiểm tra ảnh kết quả…');
      const blob = await upscalerRepository.result(job.receipt, limits.maxOutputBytes, current.abort.signal);
      await verifyUpscaleOutput(blob, source.size, scale);
      if (!mounted.current || current.abort.signal.aborted) return;
      const result = { url: URL.createObjectURL(blob), name: outputFilename(source.file.name, scale, plan.adjusted ? { width: plan.outputWidth, height: plan.outputHeight } : undefined) };
      resultUrl.current = result.url; setDownload(result);
      telemetry.complete({ exportBytes: blob.size });
      triggerUpscaleDownload(result.url, result.name);
    } catch (error) {
      if (!current.abort.signal.aborted) {
        // After a connection failure, ask the backend to stop; never assert that compute stopped.
        if (current.receipt) void upscalerRepository.cancel(current.receipt).catch(() => {});
        if (mounted.current) setFeedback({ message: error instanceof Error ? error.message : 'Không thể xử lý ảnh. Vui lòng thử lại.', error: true });
      }
    } finally {
      refreshCapabilities.current();
      if (!current.cancelling) {
        if (operation.current === current) operation.current = null;
        if (mounted.current) setBusy(false);
      }
    }
  }
  async function cancel() {
    const current = operation.current;
    if (!current || current.cancelling) return;
    current.cancelling = true;
    current.abort.abort();
    setStage('Đang gửi yêu cầu hủy…');
    if (current.receipt) {
      try { await upscalerRepository.cancel(current.receipt); if (mounted.current) setFeedback({ message: 'Đã gửi yêu cầu hủy. Máy chủ có thể cần thêm thời gian để dừng.' }); }
      catch { if (mounted.current) setFeedback({ message: 'Đã dừng chờ. Chưa xác nhận máy chủ đã hủy xử lý.', error: true }); }
    } else if (mounted.current) setFeedback({ message: 'Đã dừng tải lên. Nếu máy chủ đã nhận ảnh, xử lý có thể vẫn tiếp tục.' });
    if (operation.current === current) operation.current = null;
    if (mounted.current) setBusy(false);
  }
  const unavailable = canonicalHref ? 'Công cụ này cần mở trên DesignForge.' : checking ? 'Đang kiểm tra dịch vụ…' : capabilities?.reason === 'budget_exhausted' ? 'Ngân sách khả dụng chưa đủ cho lượt mới và mức dự phòng 100 token.' : capabilities?.reason === 'budget_unavailable' ? 'Chưa xác nhận được ngân sách. Model tạm khóa để bảo vệ chi phí.' : capabilities?.reason === 'not_configured' ? 'Dịch vụ upscale chưa được cấu hình.' : 'Dịch vụ upscale tạm thời không sẵn sàng.';
  const status = busy ? stage : feedback?.message || validation || (available ? '' : unavailable);
  const budget = capabilities?.budget;
  return <article className="image-converter-card image-upscaler-card" aria-label="Img Upscaler" aria-busy={busy || reading}>
    <h3>Img Upscaler</h3>
    <input ref={input} type="file" className="visually-hidden" accept="image/png,image/jpeg,image/webp" aria-label={tr('Chọn ảnh để upscale')} disabled={busy} onChange={event => { void choose(Array.from(event.target.files ?? [])); event.target.value = ''; }} />
    <div className={'image-converter-dropzone upscaler-dropzone' + (dragging ? ' is-dragging' : '')} onDragOver={event => { event.preventDefault(); if (!busy) setDragging(true); }} onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false); }} onDrop={event => { event.preventDefault(); setDragging(false); void choose(Array.from(event.dataTransfer.files)); }}>
      {source ? <><button type="button" className="upscaler-preview" disabled={busy || reading} onClick={() => input.current?.click()} aria-label={tr('Đổi ảnh để upscale')}><img src={source.url} alt={tr('Ảnh đã chọn để upscale')} /><span title={source.file.name}>{source.file.name}<small>{source.size.width} × {source.size.height} → {plan?.outputWidth ?? "—"} × {plan?.outputHeight ?? "—"} px</small></span></button><button type="button" className="upscaler-clear" disabled={busy || reading} onClick={() => { selection.current++; if (previewUrl.current) URL.revokeObjectURL(previewUrl.current); previewUrl.current = null; setSource(null); setFeedback(null); clearDownload(); }} aria-label={tr('Xóa ảnh đã chọn')}><Icon name="close" size={16}/></button></>
        : <button type="button" className="image-converter-placeholder" disabled={busy || reading} onClick={() => input.current?.click()}><span className="image-converter-plus" aria-hidden="true">+</span><span>{tr(reading ? 'Đang đọc ảnh…' : 'Kéo ảnh vào để upscale')}</span><small>PNG · JPG · WebP<br/>{tr('Tối đa 10 MB · 67,1 megapixel')}<br/>{tr('Đầu ra tối đa 8192 × 8192 px')}</small></button>}
    </div>
    <div className="image-converter-controls upscaler-controls">
      <div className={'upscaler-budget' + (budget && budget.remainingTokens < budget.minimumTokens ? ' is-locked' : '')}><span>{tr('Token khả dụng')}</span><strong role="status" aria-atomic="true"><span className="visually-hidden">{tr('Token khả dụng')}: </span>{budget ? `${budget.remainingTokens} / ${budget.totalTokens}` : '—'} <span>token</span></strong><details className="upscaler-budget-info"><summary aria-label={tr('Giải thích token')}>?</summary><p>{tr('Ngân sách chung, mọi người quay lại web thường xuyên để tôi có thêm 1 chút chi phí gia hạn cho mọi người dùng nhé, cảm ơn.')}</p></details></div>
      <div className="image-converter-selects"><label>{tr('Model')}<select aria-label={tr('Model upscale')} value={model} disabled={busy || checking || !available} onChange={event => { setModel(event.target.value); setFeedback(null); clearDownload(); }}>
        {(capabilities?.models.length ? capabilities.models : [{ id: 'realesrgan', name: 'Real-ESRGAN', scales: [2, 4] }]).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select></label><label>{tr('Phóng lớn')}<select aria-label={tr('Tỷ lệ upscale')} value={scale} disabled={busy} onChange={event => { setScale(Number(event.target.value) as UpscaleScale); setFeedback(null); clearDownload(); }}>{availableScales.map(value => <option key={value} value={value}>{value}×</option>)}</select></label></div>
      {busy ? <button type="button" className="image-converter-download" onClick={() => void cancel()}>{tr('Hủy xử lý')}</button> : <button type="button" className="image-converter-download" disabled={!source || reading || !available || !!validation} onClick={() => void start()}>{tr('Upscale & tải PNG')}</button>}
      {plan?.adjusted && <p className="upscaler-status" role="status">{tr('Đã tự điều chỉnh kích thước để đầu ra không vượt 8192 px mỗi cạnh.')}</p>}
      {status && <p className={'upscaler-status' + (feedback?.error || validation ? ' is-error' : '')} role={feedback?.error || validation ? 'alert' : 'status'}>{tr(status)}</p>}
      {!busy && canonicalHref ? <a className="upscaler-download-again" href={canonicalHref}>{tr('Mở trên DesignForge')}</a> : !busy && !available && <button type="button" className="upscaler-text-button" disabled={checking} onClick={() => refreshCapabilities.current()}>{tr('Kiểm tra lại dịch vụ')}</button>}
      {!busy && download && <a className="upscaler-download-again" href={download.url} download={download.name}>{tr('Tải lại PNG')}</a>}
    </div>
  </article>;
}
